/**
 * Thai Support Plugin for Vue
 * Registers Thai typography and formatting utilities globally
 */

import { vThaiTypography } from '../utils/thai-typography.js';
import { useThaiFormatters } from '../utils/thai-formatters.js';

/**
 * Thai Support Plugin
 * @param {Object} app - Vue app instance
 * @param {Object} options - Plugin options
 */
export default function ThaiSupportPlugin(app, options = {}) {
  const {
    registerDirectives = true,
    registerGlobalProperties = true,
    registerComponents = false
  } = options;

  // Register Thai typography directive
  if (registerDirectives) {
    app.directive('thai-typography', vThaiTypography);
    app.directive('thai-text', vThaiTypography);
  }

  // Register global properties for Thai formatters
  if (registerGlobalProperties) {
    const formatters = useThaiFormatters();
    
    // Add formatters to global properties
    app.config.globalProperties.$formatThaiCurrency = formatters.formatThaiCurrency;
    app.config.globalProperties.$formatThaiDate = formatters.formatThaiDate;
    app.config.globalProperties.$formatThaiTime = formatters.formatThaiTime;
    app.config.globalProperties.$formatThaiNumber = formatters.formatThaiNumber;
    app.config.globalProperties.$convertToThaiNumerals = formatters.convertToThaiNumerals;
    app.config.globalProperties.$formatThaiRelativeTime = formatters.formatThaiRelativeTime;
    app.config.globalProperties.$formatThaiFileSize = formatters.formatThaiFileSize;
    
    // Add constants
    app.config.globalProperties.$THAI_MONTHS = formatters.THAI_MONTHS;
    app.config.globalProperties.$THAI_MONTHS_SHORT = formatters.THAI_MONTHS_SHORT;
    app.config.globalProperties.$THAI_DAYS = formatters.THAI_DAYS;
    app.config.globalProperties.$THAI_DAYS_SHORT = formatters.THAI_DAYS_SHORT;
  }

  // Provide formatters for Composition API
  app.provide('thaiFormatters', useThaiFormatters());
}

/**
 * Composable for using Thai support in components
 */
export function useThaiSupport() {
  const formatters = useThaiFormatters();
  
  return {
    ...formatters,
    // Helper methods
    isThaiText: (text) => {
      if (!text || typeof text !== 'string') return false;
      return /[\u0E00-\u0E7F]/.test(text);
    },
    
    // Format helpers with common defaults
    formatPrice: (amount) => formatters.formatThaiCurrency(amount, {
      showSymbol: true,
      symbolPosition: 'suffix',
      decimals: 2
    }),
    
    formatShortDate: (date) => formatters.formatThaiDate(date, {
      format: 'short',
      useBuddhistEra: true
    }),
    
    formatLongDate: (date) => formatters.formatThaiDate(date, {
      format: 'long',
      useBuddhistEra: true,
      showDayName: true
    }),
    
    formatDisplayNumber: (number) => formatters.formatThaiNumber(number, {
      useGrouping: true
    })
  };
}